import * as XLSX from 'xlsx';
import { toast } from 'sonner';

export interface ExcelMemberData {
  name: string;
  whatsappNumber: string;
  hasLifted?: boolean;
  disbursedAmount?: number;
  liftedDate?: string; // Add lift date property
}

export const processExcelUpload = async (file: File): Promise<ExcelMemberData[]> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
          
          // Validate and process the data
          if (jsonData.length < 2) {
            throw new Error('Excel file must contain at least a header row and one data row');
          }
          
          // Find the column indices
          const headerRow = jsonData[0];
          const nameIndex = headerRow.findIndex((col: string) => 
            col?.toString().toLowerCase().includes('name'));
          const phoneIndex = headerRow.findIndex((col: string) => 
            col?.toString().toLowerCase().includes('phone') || 
            col?.toString().toLowerCase().includes('whatsapp') ||
            col?.toString().toLowerCase().includes('mobile'));
          const liftedIndex = headerRow.findIndex((col: string) => 
            col?.toString().toLowerCase().includes('lift') || 
            col?.toString().toLowerCase().includes('status'));
          const amountIndex = headerRow.findIndex((col: string) => 
            col?.toString().toLowerCase().includes('amount') || 
            col?.toString().toLowerCase().includes('disbursed'));
          const liftDateIndex = headerRow.findIndex((col: string) => 
            col?.toString().toLowerCase().includes('lift date') || 
            col?.toString().toLowerCase().includes('date of lift'));
          
          if (nameIndex === -1 || phoneIndex === -1) {
            throw new Error('Excel file must contain columns for Name and WhatsApp Number/Phone');
          }
          
          // Process data rows
          const members: ExcelMemberData[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row[nameIndex] || !row[phoneIndex]) continue; // Skip rows with empty name or phone
            
            const member: ExcelMemberData = {
              name: row[nameIndex].toString().trim(),
              whatsappNumber: row[phoneIndex].toString().trim(),
            };
            
            if (liftedIndex !== -1 && row[liftedIndex]) {
              const liftedValue = row[liftedIndex].toString().toLowerCase();
              member.hasLifted = liftedValue === 'yes' || 
                                liftedValue === 'true' || 
                                liftedValue === '1' ||
                                liftedValue === 'lifted';
            }
            
            if (amountIndex !== -1 && row[amountIndex]) {
              const amountValue = parseFloat(row[amountIndex]);
              if (!isNaN(amountValue)) {
                member.disbursedAmount = amountValue;
              }
            }
            
            // Process lift date if available
            if (liftDateIndex !== -1 && row[liftDateIndex]) {
              let liftDate = row[liftDateIndex];
              
              // If the date is an Excel date number, convert it to a string date
              if (typeof liftDate === 'number') {
                liftDate = XLSX.SSF.format('yyyy-mm-dd', liftDate);
              } else {
                liftDate = liftDate.toString().trim();
              }
              
              // Store the date string
              member.liftedDate = liftDate;
            }
            
            members.push(member);
          }
          
          resolve(members);
        } catch (error) {
          console.error('Error processing Excel file:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      reject(error);
    }
  });
};

export const downloadMemberTemplate = (chitPoolName: string = 'Members') => {
  try {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ['Name', 'WhatsApp Number', 'Has Lifted', 'Disbursed Amount', 'Lift Date'],
      ['John Doe', '+919876543210', 'No', '', ''],
      ['Jane Smith', '+919876543211', 'Yes', '95000', '2023-10-15']
    ]);
    
    // Set column widths
    const wscols = [
      { wch: 20 }, // Name
      { wch: 15 }, // WhatsApp Number
      { wch: 10 }, // Has Lifted
      { wch: 15 }, // Disbursed Amount
      { wch: 15 }  // Lift Date
    ];
    ws['!cols'] = wscols;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    
    // Generate and download the file
    XLSX.writeFile(wb, `${chitPoolName.replace(/[^a-zA-Z0-9]/g, '_')}_members_template.xlsx`);
    
    toast.success('Template downloaded successfully');
  } catch (error) {
    console.error('Error generating template:', error);
    toast.error('Failed to generate template');
  }
};
